import type { BlockedPermissions } from '../@types/BlockedPermissions';
import type { Actions } from '../actions';
import {
  BLOCKED_PERMISSIONS_SET_SHOULD_SHOW_MODAL,
  RCV_BLOCKED_PERMISSIONS_GET,
  RCV_BLOCKED_PERMISSIONS_REMOVE,
  RCV_BLOCKED_PERMISSIONS_SET,
  REQ_BLOCKED_PERMISSIONS_GET,
  REQ_BLOCKED_PERMISSIONS_REMOVE,
  REQ_BLOCKED_PERMISSIONS_SET,
  REQUEST_FAILED,
} from '../actions/blocked-permissions';
import { requestStatus, responseStatus } from '../shared/statuses';

/* ----------------- INITIAL STATE ----------------- */

interface BlockedPermissionsState {
  requestStatus: number;
  responseStatus: number;
  previousRequest: number | null;
  blockedPermissions: BlockedPermissions | null;
  lastLookupEntityId: string | null;
  shouldShowModal: boolean;
}

const _initialState: BlockedPermissionsState = {
  requestStatus: requestStatus.WAITING,
  responseStatus: responseStatus.OK,
  previousRequest: null,
  blockedPermissions: null,
  lastLookupEntityId: null,
  shouldShowModal: false,
};

/* ----------------- STATUS HELPERS ----------------- */

const getSuccessfulResponse = (newState: BlockedPermissionsState) => {
  return {
    ...newState,
    requestStatus: requestStatus.WAITING,
    responseStatus: responseStatus.OK,
  };
};

const getPendingRequest = (state: BlockedPermissionsState, status: number) => {
  return {
    ...state,
    requestStatus: status,
    responseStatus: responseStatus.PENDING,
  };
};

/* ----------------- REDUCER ----------------- */

const blockedPermissionsReducer = (
  state: BlockedPermissionsState = _initialState,
  action: Actions,
): BlockedPermissionsState => {
  switch (action.type) {
    case BLOCKED_PERMISSIONS_SET_SHOULD_SHOW_MODAL:
      return { ...state, shouldShowModal: action.value };

    case REQ_BLOCKED_PERMISSIONS_GET:
      return getPendingRequest(
        { ...state, lastLookupEntityId: action.entityId, blockedPermissions: null },
        requestStatus.FETCHING,
      );

    case REQ_BLOCKED_PERMISSIONS_SET:
      return getPendingRequest(state, requestStatus.CREATING);

    case REQ_BLOCKED_PERMISSIONS_REMOVE:
      return getPendingRequest(state, requestStatus.REMOVING);

    case RCV_BLOCKED_PERMISSIONS_GET:
      return getSuccessfulResponse({ ...state, blockedPermissions: action.response });

    case RCV_BLOCKED_PERMISSIONS_SET:
      return getSuccessfulResponse({
        ...state,
        blockedPermissions: action.response,
        shouldShowModal: false,
      });

    case RCV_BLOCKED_PERMISSIONS_REMOVE:
      return getSuccessfulResponse({ ...state, blockedPermissions: null, shouldShowModal: false });

    case REQUEST_FAILED:
      return {
        ...state,
        requestStatus: requestStatus.WAITING,
        responseStatus: responseStatus.FAILED,
        previousRequest: state.requestStatus,
      };

    default:
      return state;
  }
};

export default blockedPermissionsReducer;
